import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Settings, BarChart3, UserCheck, Calendar, DollarSign, TrendingUp, Activity, AlertCircle, Eye, Clock, Star } from 'lucide-react'
import { useAppStore } from '../lib/store'
import { toast } from '../lib/toast'
import type { User, Booking, Service } from '../types'

interface AdminStats {
  totalUsers: number
  totalClients: number
  totalProviders: number
  totalBookings: number
  totalRevenue: number
  pendingApprovals: number
  activeServices: number
  monthlyGrowth: number
  completedToday: number
  averageBookingValue: number
}

export default function AdminDashboard() {
  const { user, setUser, users, bookings, services, getRealUsers, getRealBookings, getRealServices } = useAppStore()
  const navigate = useNavigate()
  const [stats, setStats] = React.useState<AdminStats>({
    totalUsers: 0,
    totalClients: 0,
    totalProviders: 0,
    totalBookings: 0,
    totalRevenue: 0,
    pendingApprovals: 0,
    activeServices: 0,
    monthlyGrowth: 0,
    completedToday: 0,
    averageBookingValue: 0
  })

  React.useEffect(() => {
    // Check if this is a real admin or demo admin
    const demoAdminEmails = ['sarah.j@email.com', 'mike.w@email.com', 'emily.d@email.com', 'robert.b@email.com', 'lisa.a@email.com', 'admin@demo.com', 'test@admin.com'];
    const isDemoAdmin = user && demoAdminEmails.includes(user.email);
    
    // Debug logging
    console.log('AdminDashboard - User email:', user?.email);
    console.log('AdminDashboard - Is demo admin:', isDemoAdmin);
    console.log('AdminDashboard - Total users before filter:', users.length);
    console.log('AdminDashboard - Real users after filter:', getRealUsers().length);
    
    // Use real data for real admins, all data for demo admins
    const usersData = isDemoAdmin ? users : getRealUsers();
    const bookingsData = isDemoAdmin ? bookings : getRealBookings();
    const servicesData = isDemoAdmin ? services : getRealServices();
    
    // Calculate real stats from actual data
    const calculatedStats: AdminStats = {
      totalUsers: usersData.length,
      totalClients: usersData.filter((u: User) => u.role === 'client').length,
      totalProviders: usersData.filter((u: User) => u.role === 'provider').length,
      totalBookings: bookingsData.length,
      totalRevenue: bookingsData.reduce((sum: number, booking: Booking) => sum + booking.amount, 0),
      pendingApprovals: usersData.filter((u: User) => u.status === 'inactive').length,
      activeServices: servicesData.filter((s: Service) => s.status === 'active').length,
      monthlyGrowth: 15.3, // Would calculate from real data in production
      completedToday: bookingsData.filter((b: Booking) => b.status === 'completed').length,
      averageBookingValue: bookingsData.length > 0 ? bookingsData.reduce((sum: number, booking: Booking) => sum + booking.amount, 0) / bookingsData.length : 0
    }
    setStats(calculatedStats)
  }, [user, users, bookings, services, getRealUsers, getRealBookings, getRealServices])

  const handleLogout = () => {
    setUser(null)
    navigate('/')
  }

  const handleNavigation = (path: string) => {
    toast.info(`${path} feature coming soon!`)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8">
          <button
            onClick={() => handleNavigation('User Management')}
            className="bg-white dark:bg-dark-card p-3 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border hover:shadow-md transition-shadow text-left"
          >
            <Users className="w-4 h-4 md:w-8 md:h-8 text-blue-500 mb-1 md:mb-3" />
            <h3 className="text-xs md:text-sm md:font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Users</h3>
            <p className="text-xs text-gray-600 dark:text-dark-text-secondary hidden md:block">Manage all users</p>
            <div className="mt-1 md:mt-2 text-sm md:text-2xl font-bold text-blue-600">{stats.totalUsers}</div>
          </button>

          <button
            onClick={() => handleNavigation('Service Management')}
            className="bg-white dark:bg-dark-card p-3 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border hover:shadow-md transition-shadow text-left"
          >
            <Settings className="w-4 h-4 md:w-8 md:h-8 text-green-500 mb-1 md:mb-3" />
            <h3 className="text-xs md:text-sm md:font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Services</h3>
            <p className="text-xs text-gray-600 dark:text-dark-text-secondary hidden md:block">Manage services</p>
            <div className="mt-1 md:mt-2 text-sm md:text-2xl font-bold text-green-600">{stats.activeServices}</div>
          </button>

          <button
            onClick={() => handleNavigation('Analytics')}
            className="bg-white dark:bg-dark-card p-3 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border hover:shadow-md transition-shadow text-left"
          >
            <BarChart3 className="w-4 h-4 md:w-8 md:h-8 text-purple-500 mb-1 md:mb-3" />
            <h3 className="text-xs md:text-sm md:font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Analytics</h3>
            <p className="text-xs text-gray-600 dark:text-dark-text-secondary hidden md:block">View reports</p>
            <div className="mt-1 md:mt-2 text-sm md:text-2xl font-bold text-purple-600">{stats.monthlyGrowth}%</div>
          </button>

          <button
            onClick={() => handleNavigation('Provider Verification')}
            className="bg-white dark:bg-dark-card p-3 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border hover:shadow-md transition-shadow text-left"
          >
            <UserCheck className="w-4 h-4 md:w-8 md:h-8 text-orange-500 mb-1 md:mb-3" />
            <h3 className="text-xs md:text-sm md:font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Verifications</h3>
            <p className="text-xs text-gray-600 dark:text-dark-text-secondary hidden md:block">Pending approvals</p>
            <div className="mt-1 md:mt-2 text-sm md:text-2xl font-bold text-orange-600">{stats.pendingApprovals}</div>
          </button>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8">
          <div className="bg-white dark:bg-dark-card p-3 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <h3 className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Revenue</h3>
              <DollarSign className="w-3 h-3 md:w-5 md:h-5 text-green-500" />
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900 dark:text-dark-text-primary">${stats.totalRevenue.toLocaleString()}</p>
            <p className="text-xs md:text-sm text-green-600 mt-1 md:mt-2">+12.5% from last month</p>
          </div>

          <div className="bg-white dark:bg-dark-card p-3 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <h3 className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Bookings</h3>
              <Calendar className="w-3 h-3 md:w-5 md:h-5 text-blue-500" />
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900 dark:text-dark-text-primary">{stats.totalBookings.toLocaleString()}</p>
            <p className="text-xs md:text-sm text-blue-600 mt-1 md:mt-2">{stats.completedToday} completed today</p>
          </div>

          <div className="bg-white dark:bg-dark-card p-3 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <h3 className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Active Users</h3>
              <Activity className="w-3 h-3 md:w-5 md:h-5 text-purple-500" />
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900 dark:text-dark-text-primary">{stats.totalUsers.toLocaleString()}</p>
            <div className="flex justify-between mt-1 md:mt-2 text-xs md:text-sm">
              <span className="text-blue-600">{stats.totalClients} clients</span>
              <span className="text-green-600">{stats.totalProviders} providers</span>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-card p-3 md:p-6 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <h3 className="text-xs md:text-sm font-medium text-gray-600 dark:text-dark-text-secondary">Avg Value</h3>
              <TrendingUp className="w-3 h-3 md:w-5 md:h-5 text-orange-500" />
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900 dark:text-dark-text-primary">${stats.averageBookingValue}</p>
            <p className="text-xs md:text-sm text-orange-600 mt-1 md:mt-2">+8.2% growth</p>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-3 md:gap-8">
          {/* Recent Users */}
          <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="px-3 md:px-6 py-3 md:py-4 border-b border-gray-200 dark:border-dark-border">
              <h2 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Recent User Activity</h2>
            </div>
            <div className="p-3 md:p-6">
              <div className="space-y-2 md:space-y-4">
                <div className="flex items-center justify-between p-2 md:p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="w-3 h-3 md:w-4 md:h-4 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">john.doe@example.com</p>
                      <p className="text-xs text-gray-600 dark:text-dark-text-secondary">Client • 2h ago</p>
                    </div>
                  </div>
                  <Eye className="w-3 h-3 md:w-4 md:h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                </div>

                <div className="flex items-center justify-between p-2 md:p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <div className="w-6 h-6 md:w-8 md:h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <UserCheck className="w-3 h-3 md:w-4 md:h-4 text-green-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">jane.smith@clean.com</p>
                      <p className="text-xs text-gray-600 dark:text-dark-text-secondary">Provider • Today</p>
                    </div>
                  </div>
                  <AlertCircle className="w-3 h-3 md:w-4 md:h-4 text-yellow-500 flex-shrink-0" />
                </div>

                <div className="flex items-center justify-between p-2 md:p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-2 md:space-x-3">
                    <div className="w-6 h-6 md:w-8 md:h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-3 h-3 md:w-4 md:h-4 text-purple-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs md:text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">mike.wilson@service.com</p>
                      <p className="text-xs text-gray-600 dark:text-dark-text-secondary">Provider • Pending</p>
                    </div>
                  </div>
                  <AlertCircle className="w-3 h-3 md:w-4 md:h-4 text-red-500 flex-shrink-0" />
                </div>
              </div>
            </div>
          </div>

          {/* System Health */}
          <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="px-3 md:px-6 py-3 md:py-4 border-b border-gray-200 dark:border-dark-border">
              <h2 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-dark-text-primary">System Health</h2>
            </div>
            <div className="p-3 md:p-6">
              <div className="space-y-2 md:space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">Database</span>
                  <span className="px-1 md:px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs md:text-sm rounded-full">Healthy</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">API Response</span>
                  <span className="text-xs md:text-sm text-gray-900 dark:text-dark-text-primary">124ms</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">Sessions</span>
                  <span className="text-xs md:text-sm text-gray-900 dark:text-dark-text-primary">1,247</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">Uptime</span>
                  <span className="text-xs md:text-sm text-gray-900 dark:text-dark-text-primary">99.9%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-600 dark:text-dark-text-secondary">Last Backup</span>
                  <span className="text-xs md:text-sm text-gray-900 dark:text-dark-text-primary">2h ago</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
